import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { keyframes } from '@emotion/react';
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { verifyPayment } from '../utils/api';

// Import section components
import {
    HeroSection,
    TrustSection,
    HowItWorksSection,
    WhyParticipateSection,
    WorkAreasSection,
    AboutSection,
    SupportSection,
    ContactSection,
    FooterSection
} from '../components/sections';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Fade-in component
const FadeInSection = ({ children, delay = 0 }) => {
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    return (
        <Box
            ref={ref}
            sx={{
                opacity: inView ? 1 : 0,
                animation: inView ? `${fadeInUp} 0.4s ease-out ${delay}s both` : 'none'
            }}
        >
            {children}
        </Box>
    );
};

const LandingPage = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
    const [paymentVerified, setPaymentVerified] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setMobileOpen(false);
    };

    const handleStartTest = () => {
        navigate('/zmiana-pracy');
    };

    // Check for payment success in URL and verify with backend
    useEffect(() => {
        const verifyPaymentStatus = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const paymentStatus = urlParams.get('payment');
            const sessionId = urlParams.get('session_id');

            if (paymentStatus === 'success' && sessionId) {
                setIsVerifyingPayment(true);
                setShowPaymentDialog(true);
                setPaymentError(null);

                try {
                    // Verify payment with backend
                    const verification = await verifyPayment(sessionId);

                    if (verification.verified) {
                        // Payment confirmed
                        setPaymentVerified(true);

                        // Clean up URL
                        window.history.replaceState({}, document.title, window.location.pathname);

                        // Store payment info in localStorage
                        localStorage.setItem('therapp_early_supporter', JSON.stringify({
                            verified: true,
                            timestamp: new Date().toISOString(),
                            sessionId: sessionId
                        }));
                    } else {
                        // Payment not yet confirmed
                        setPaymentError(
                            'Płatność nie została jeszcze potwierdzona. To może potrwać kilka sekund. Odśwież stronę za chwilę.'
                        );
                    }
                } catch (error) {
                    console.error('Payment verification error:', error);
                    setPaymentError(
                        'Nie udało się zweryfikować płatności. Jeśli dokonałeś płatności, odśwież stronę za chwilę lub skontaktuj się z nami.'
                    );
                } finally {
                    setIsVerifyingPayment(false);
                }
            } else if (paymentStatus === 'cancelled') {
                // User cancelled payment
                setShowPaymentDialog(true);
                setPaymentError('Płatność została anulowana.');
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        };

        verifyPaymentStatus();
    }, [navigate]);

    const navigationItems = [
        { label: 'Jak działa aplikacja?', id: 'how-it-works' },
        { label: 'Obszary pracy aplikacji', id: 'work-areas' },
        { label: 'O nas', id: 'about-us' },
        { label: 'Pomóż ulepszyć aplikację', id: 'support-us' },
        { label: 'Kontakt', id: 'contact' }
    ];

    const drawer = (
        <Box sx={{ width: 250, height: '100%', bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src="/logo-192x192.png"
                        alt="Find solution Logo"
                        style={{ height: '24px', width: '24px', marginRight: '8px' }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#616161' }}>
                        Find solution
                    </Typography>
                </Box>
                <IconButton onClick={handleDrawerToggle}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <List>
                {navigationItems.map((item) => (
                    <ListItem key={item.id} disablePadding>
                        <ListItemButton onClick={() => scrollToSection(item.id)}>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
                <ListItem disablePadding>
                    <ListItemButton onClick={handleStartTest}>
                        <ListItemText
                            primary="Zbuduj swój plan działania"
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontWeight: 'bold',
                                    color: 'primary.main'
                                }
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box>
            {/* Header */}
            <AppBar
                position="sticky"
                sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
                }}
            >
                <Toolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        <img
                            src="/logo-192x192.png"
                            alt="Find solution Logo"
                            style={{ height: '32px', width: '32px', marginRight: '12px' }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                color: '#616161'
                            }}
                        >
                            Findsolution.eu
                        </Typography>
                    </Box>

                    {!isMobile ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            {navigationItems.map((item) => (
                                <Button
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    sx={{
                                        color: 'grey.700',
                                        '&:hover': {
                                            bgcolor: 'grey.100'
                                        }
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <Button
                                onClick={handleStartTest}
                                sx={{
                                    background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                    color: 'white',
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 8px 25px rgba(199, 154, 95, 0.3)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Zbuduj swój plan działania
                            </Button>
                        </Box>
                    ) : (
                        <IconButton onClick={handleDrawerToggle} sx={{ color: 'primary.main' }}>
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
            >
                {drawer}
            </Drawer>

            {/* Page Sections */}
            <FadeInSection>
                <HeroSection />
            </FadeInSection>

            <FadeInSection delay={0.2}>
                <TrustSection />
            </FadeInSection>

            <HowItWorksSection FadeInSection={FadeInSection} />

            <WhyParticipateSection FadeInSection={FadeInSection} />

            <WorkAreasSection FadeInSection={FadeInSection} />

            <FadeInSection delay={0.4}>
                <AboutSection />
            </FadeInSection>

            <SupportSection FadeInSection={FadeInSection} />

            <FadeInSection delay={0.6}>
                <ContactSection />
            </FadeInSection>

            <FooterSection FadeInSection={FadeInSection} />

            {/* Payment Verification Dialog */}
            <Dialog
                open={showPaymentDialog}
                onClose={() => !isVerifyingPayment && setShowPaymentDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {isVerifyingPayment && 'Weryfikowanie płatności...'}
                    {!isVerifyingPayment && paymentVerified && 'Płatność potwierdzona!'}
                    {!isVerifyingPayment && paymentError && 'Status płatności'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        py: 2
                    }}>
                        {isVerifyingPayment && (
                            <>
                                <CircularProgress size={60} />
                                <Typography variant="body1" color="text.secondary" textAlign="center">
                                    Sprawdzamy status Twojej płatności w systemie Stripe...
                                </Typography>
                            </>
                        )}

                        {!isVerifyingPayment && paymentVerified && (
                            <>
                                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
                                <Typography variant="h6" color="success.main" textAlign="center">
                                    Dziękujemy za wsparcie!
                                </Typography>
                                <Typography variant="body1" color="text.secondary" textAlign="center">
                                    Twoja płatność została potwierdzona.
                                </Typography>
                                <Typography variant="body2" color="primary.main" textAlign="center" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Pamiętaj: masz dożywotni 20% rabat na pełną wersję! 🎉
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate('/ankieta')}
                                        sx={{
                                            flex: 1,
                                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                                transform: 'scale(1.05)'
                                            }
                                        }}
                                    >
                                        Przejdź do aplikacji
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowPaymentDialog(false)}
                                        sx={{ flex: 1 }}
                                    >
                                        Zamknij
                                    </Button>
                                </Box>
                            </>
                        )}

                        {!isVerifyingPayment && paymentError && (
                            <>
                                <Alert severity={paymentError.includes('anulowana') ? 'info' : 'warning'} sx={{ width: '100%' }}>
                                    {paymentError}
                                </Alert>
                                {!paymentError.includes('anulowana') && (
                                    <Button
                                        variant="contained"
                                        onClick={() => window.location.reload()}
                                        sx={{ mt: 2 }}
                                    >
                                        Odśwież stronę
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default LandingPage;